import capitalize from 'lodash/capitalize';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { Link } from 'react-router';
import { billing, format } from 'libs';
import { RevokedProAccessBrokerageBanner } from 'components/banners';
import { SolidStarIcon } from 'icons';
import { Button, TagPill } from 'components/common';
import UpgradeAccountForm from './UpgradeAccountForm';
import type { PaymentFormSubmitData, SubscriptionPrice } from './types';

interface Subscription {
  active_discount?: { coupon: { percent_off?: number } };
  ends_at?: string;
  id?: number;
  price: {
    frequency?: string;
    name?: string;
    price?: number;
  };
  stripe_status?: string;
  trial_ends_at?: string;
  type?: string;
}

interface CurrentSubscriptionsProps {
  canStartAddonFreeTrial: boolean;
  isProcessing: boolean;
  missingTrialAddonNames: string;
  missingTrialAddonPrices: SubscriptionPrice[];
  onAddPaymentMethod: (data: PaymentFormSubmitData) => void | Promise<void>;
  onStartAddonFreeTrial: () => void;
  subscriptions: Subscription[];
  user: {
    brokerage: { name?: string };
    features: {
      has_brokerage?: boolean;
      has_finmo_connection?: boolean;
      has_past_due_payments?: boolean;
      has_payment_method?: boolean;
      on_platinum_grace_period?: boolean;
    };
  };
}

const isSubscriptionOnTrial = (subscription: Subscription) =>
  Boolean(subscription.trial_ends_at) && dayjs(subscription.trial_ends_at).isAfter(dayjs());

const getSubscriptionPrice = (subscription: Subscription) =>
  subscription.active_discount
    ? billing.discount(subscription.price, subscription.active_discount.coupon)
    : subscription.price.price!;

const CurrentSubscriptions = ({
  canStartAddonFreeTrial,
  isProcessing,
  missingTrialAddonNames,
  missingTrialAddonPrices,
  onAddPaymentMethod,
  onStartAddonFreeTrial,
  subscriptions,
  user,
}: CurrentSubscriptionsProps) => {
  const {
    brokerage,
    features: { has_brokerage, has_finmo_connection, has_payment_method, on_platinum_grace_period },
  } = user;

  const platinumEndsAt = subscriptions.find((subscription) => subscription.type === 'platinum')?.ends_at;

  const sortedSubscriptions = subscriptions
    .filter((subscription) => !subscription.ends_at)
    .sort((a, b) => b.price.price! - a.price.price!);

  const subscriptionCost =
    sortedSubscriptions
      .filter((subscription) => !isSubscriptionOnTrial(subscription))
      .reduce((total, subscription) => total + getSubscriptionPrice(subscription), 0) / 100;

  const trialSubscriptionCost =
    sortedSubscriptions
      .filter((subscription) => isSubscriptionOnTrial(subscription))
      .reduce((total, subscription) => total + getSubscriptionPrice(subscription), 0) / 100;
  const trialEndsAt = sortedSubscriptions.find((subscription) => isSubscriptionOnTrial(subscription))?.trial_ends_at;

  return (
    <div className="card bg-mystic border-0 flex-column" data-testid="subscriptions-current-subscription-card">
      <div className="card-body d-xl-flex flex-row justify-content-between">
        <div className="flex-basis-50 flex-column pe-2">
          <h1 className="fw-bold mb-8 text-2_25">Your Lender Spotlight account</h1>
          <h3 className="fw-bold mb-3 text-1_5">Current subscriptions</h3>
          <div className="mb-4">
            You currently have access to 60+ lenders, advanced filtering, policies and guidelines, compare and share
            reports as well as the option of purchasing add-ons such as the AI Assistant.
          </div>
          {on_platinum_grace_period && !has_brokerage && (
            <div className="mb-4">
              Your Platinum subscription is scheduled to downgrade on{' '}
              <span className="fw-bold">{dayjs(platinumEndsAt).format('dddd, MMMM D, YYYY')}</span>.
            </div>
          )}
          {subscriptionCost > 0 && (
            <div className="my-4">
              You&apos;ll be charged <span className="fw-bold">{format.currency(subscriptionCost)}</span> for your
              active paid subscriptions.
            </div>
          )}
          {trialSubscriptionCost > 0 && (
            <div className="my-4">
              You&apos;ll be charged <span className="fw-bold">{format.currency(trialSubscriptionCost)}</span> for your
              subscriptions once their free trials end on{' '}
              <span className="fw-bold">{dayjs(trialEndsAt).format('dddd, MMMM D, YYYY')}</span>.
            </div>
          )}
          {has_finmo_connection && (
            <div className="mb-3">
              <h4 className="mb-0 text-1_125" data-testid="subscriptions-current-subscription-card-subtitle-has-finmo">
                <span className="fw-bold">Platinum Finmo</span>
              </h4>
              <div data-testid="subscriptions-current-subscription-card-description-has-finmo">
                Included with your Finmo account
              </div>
            </div>
          )}
          {has_brokerage && (
            <div className="mb-3">
              <h4
                className="mb-0 text-1_125"
                data-testid="subscriptions-current-subscription-card-subtitle-brokerage-team"
              >
                <span className="fw-bold">Platinum Team</span>
              </h4>
              <div data-testid="subscriptions-current-subscription-card-description-brokerage-team">
                Under {brokerage.name}&apos;s team subscription
              </div>
            </div>
          )}
          {sortedSubscriptions.map((subscription) => {
            const onTrial = isSubscriptionOnTrial(subscription);

            return (
              <div
                className="mb-3"
                data-testid={`subscriptions-current-subscription-card-${subscription.type}`}
                key={subscription.id}
              >
                <div className="d-flex align-items-center">
                  <h4
                    className="mb-0 text-1_125"
                    data-testid={`subscriptions-current-subscription-card-subtitle-${subscription.type}`}
                  >
                    <span className="fw-bold">{subscription.price.name}</span>
                  </h4>
                  {subscription.active_discount && (
                    <span className="ms-2">
                      <TagPill icon={<SolidStarIcon />}>One month discount</TagPill>
                    </span>
                  )}
                  {onTrial && (
                    <span className="ms-2">
                      <TagPill>Free trial</TagPill>
                    </span>
                  )}
                  {subscription.stripe_status === 'past_due' && (
                    <Link className="ms-2" to="/settings/payment-method">
                      <TagPill color="red">Payment Overdue</TagPill>
                    </Link>
                  )}
                </div>
                <div data-testid={`subscriptions-current-subscription-card-description-${subscription.type}`}>
                  <span
                    className={classnames('fw-bold', {
                      'pe-1 text-dark-gray text-line-through': subscription.active_discount || onTrial,
                    })}
                  >
                    ${subscription.price.price! / 100} / {capitalize(subscription.price.frequency)}
                  </span>
                  {onTrial && <span className="fw-bold">$0</span>}
                  {subscription.active_discount && !onTrial && (
                    <span className="fw-bold">
                      {format.currency(billing.discount(subscription.price, subscription.active_discount.coupon) / 100)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="d-flex flex-basis-50 justify-content-center">
          <UpgradeAccountForm
            confirmation={false}
            description={
              has_payment_method
                ? canStartAddonFreeTrial
                  ? `Start your free trial for ${missingTrialAddonNames}.`
                  : 'A separate invoice will be emailed to you for each subscription.'
                : canStartAddonFreeTrial
                  ? `Enter your payment details to start your free trial for ${missingTrialAddonNames}.`
                  : 'Enter your payment details to purchase subscriptions.'
            }
            footer={has_payment_method ? null : 'A separate invoice will be emailed to you for each subscription.'}
            header={
              canStartAddonFreeTrial ? (
                <h4 className="fw-bold">Start your 30-day free trial</h4>
              ) : has_payment_method ? null : (
                <h4 className="fw-bold">Add payment method</h4>
              )
            }
            isSubmitting={isProcessing}
            onSubmit={onAddPaymentMethod}
            paymentButtonLabel={canStartAddonFreeTrial ? 'Start my 30-day free trial' : 'Add payment method'}
            paymentDetailsLabel={null}
            savedCardAction={
              canStartAddonFreeTrial ? (
                <div>
                  <Button disabled={isProcessing} onClick={onStartAddonFreeTrial} text="bold" variant="primary-dark">
                    Start my 30-day free trial
                  </Button>
                </div>
              ) : (
                <Link
                  className="d-block"
                  data-testid="subscriptions-addon-subscription-update-payment"
                  to="/settings/payment-method"
                >
                  Update payment method
                </Link>
              )
            }
            trial={{ prices: canStartAddonFreeTrial ? missingTrialAddonPrices : [] }}
            user={user}
          />
        </div>
      </div>
      <div className="mx-3">
        <RevokedProAccessBrokerageBanner isDismissable={false} />
      </div>
    </div>
  );
};

export default CurrentSubscriptions;
